import { useMemo } from 'react';

import { Drawer, Progress } from 'antd';
import { useIntl } from 'react-intl';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import type { FilterContainerActionButton } from '@src/components/FilterContainer/FilterContainer.interfaces';
import { formatUserFullName } from '@src/util/format/userFullName';
import IntlMessages from '@src/util/IntlMessages';

import MarkAttendanceConfirmModal from './components/MarkAttendanceConfirmModal';
import MarkAttendanceInProgressStep from './components/MarkAttendanceInProgressStep';
import MarkAttendanceUserStep from './components/MarkAttendanceUserStep';
import { useMarkAttendanceDrawer } from './hooks/useMarkAttendanceDrawer';
import type { MarkAttendanceUserRow } from './types';

import styles from './MarkAttendanceDrawer.module.less';

type MarkAttendanceDrawerProps = {
  open: boolean;
  onClose: () => void;
  /** When provided, the drawer opens directly on step 2 with this user preselected. */
  initialUser?: MarkAttendanceUserRow | null;
  /** When provided, the first step lists only members of the selected journey instance. */
  initialUsers?: MarkAttendanceUserRow[] | null;
  /** Today's journey name by employee id — labels org-wide Elastic user rows. */
  journeyNameByEmployeeId?: Record<string, string> | null;
  /** Called when a picked user has not started the journey — parent opens the start modal. */
  onStartJourneyInstead: (user: MarkAttendanceUserRow) => void;
};

const MarkAttendanceDrawer = ({
  open,
  onClose,
  initialUser,
  initialUsers,
  journeyNameByEmployeeId = null,
  onStartJourneyInstead,
}: MarkAttendanceDrawerProps) => {
  const intl = useIntl();
  const {
    step,
    search,
    setSearch,
    selectedUser,
    journeyDetails,
    journeyTimeline,
    journeyDetailsLoading,
    journeyDetailsError,
    hasJourneyEnded,
    isOnBreak,
    canStartBreak,
    canEndJourney,
    users,
    usersLoading,
    currentPage,
    pageSize,
    totalCount,
    handlePageChange,
    handleSelectUser,
    handleBack,
    handleStartBreak,
    handleEndBreak,
    handleEndJourney,
    confirmModalOpen,
    confirmKind,
    confirmMarkedAt,
    confirmSubmitting,
    handleConfirmMark,
    handleCancelConfirm,
    handleMarkedAtChange,
  } = useMarkAttendanceDrawer({
    open,
    initialUser,
    initialUsers,
    journeyNameByEmployeeId,
    onStartJourneyInstead,
  });

  const goBack = () => {
    // Opened from a row "Marcar" — skip the user list; back closes the drawer.
    if (step === 2 && !initialUser) {
      handleBack();
      return;
    }
    onClose();
  };

  const canShowStep2Actions =
    step === 2 && !journeyDetailsLoading && !journeyDetailsError;

  const actionButtons = useMemo((): FilterContainerActionButton[] => {
    if (!canShowStep2Actions || hasJourneyEnded) {
      return [];
    }

    const buttons: FilterContainerActionButton[] = [];

    if (isOnBreak) {
      buttons.push({
        type: 'primary',
        label: intl.formatMessage({ id: 'workJourney.realTime.markAttendance.endBreak' }),
        action: handleEndBreak,
      });
    } else if (canStartBreak) {
      buttons.push({
        type: 'primary',
        label: intl.formatMessage({ id: 'workJourney.realTime.markAttendance.startBreak' }),
        action: handleStartBreak,
      });
    }

    if (canEndJourney) {
      buttons.push({
        type: 'default',
        label: intl.formatMessage({ id: 'workJourney.realTime.markAttendance.endJourney' }),
        action: handleEndJourney,
      });
    }

    return buttons;
  }, [
    canEndJourney,
    canShowStep2Actions,
    canStartBreak,
    handleEndBreak,
    handleEndJourney,
    handleStartBreak,
    hasJourneyEnded,
    intl,
    isOnBreak,
  ]);

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        closable={false}
        footer={null}
        title={null}
        width="54vw"
        className={styles.drawerRoot}
        destroyOnClose
      >
        <BoxContainer>
          <BoxContainer content>
            <FilterContainer
              goBack={goBack}
              title={
                <Title.Header value={<IntlMessages id="workJourney.realTime.markAttendance.title" />} />
              }
              actionButtons={actionButtons}
            />
          </BoxContainer>
          <BoxContainer
            content
            loading={step === 2 && journeyDetailsLoading}
            className={styles.content}
          >
            <div className={styles.progressWrap}>
              <Progress
                className={styles.progressBar}
                percent={step === 1 ? 50 : 100}
                showInfo={false}
                strokeWidth={6}
              />
              <span className={styles.progressLabel}>
                {intl.formatMessage(
                  { id: 'workJourney.realTime.markAttendance.stepOf' },
                  { current: step, total: 2 },
                )}
              </span>
            </div>
            <div className={styles.scrollArea}>
              {step === 1 ? (
                <MarkAttendanceUserStep
                  users={users}
                  loading={usersLoading}
                  search={search}
                  onSearchChange={setSearch}
                  onSelectUser={handleSelectUser}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={handlePageChange}
                />
              ) : journeyDetailsLoading || !selectedUser ? null : (
                <MarkAttendanceInProgressStep
                  user={selectedUser}
                  journeyDetails={journeyDetails}
                  journeyTimeline={journeyTimeline}
                  error={journeyDetailsError}
                />
              )}
            </div>
          </BoxContainer>
        </BoxContainer>
      </Drawer>

      <MarkAttendanceConfirmModal
        open={confirmModalOpen}
        kind={confirmKind}
        userDisplayName={
          selectedUser
            ? formatUserFullName(selectedUser) || selectedUser.fullName?.trim() || ''
            : ''
        }
        markedAt={confirmMarkedAt}
        journeyDetails={journeyDetails}
        submitting={confirmSubmitting}
        onConfirm={handleConfirmMark}
        onCancel={handleCancelConfirm}
        onMarkedAtChange={handleMarkedAtChange}
      />
    </>
  );
};

export default MarkAttendanceDrawer;
